// Single source of truth for copy that must stay in lockstep with the data it
// describes. Anything rendered as a claim about the numbers (a night window,
// an altitude, a pluralized count, a "this is a pattern" superlative, or the
// live-poll cadence) should derive from here rather than being restated as a
// literal in each surface.

// ---------------------------------------------------------------------------
// Night window — hour >= 23 || hour < 5, in America/New_York. The boundary
// must match `isLateNight` in headline-features.js (web) and the worker's
// aggregates.ts hour check. Labels are derived from the same hours so copy
// can never drift from the computation.
// ---------------------------------------------------------------------------
export const NIGHT_WINDOW = {
  startHour: 23,
  endHour: 5,
  rangeLabel: '11pm–5am',
  afterLabel: 'after 11pm',
};

// ---------------------------------------------------------------------------
// Altitude formatting — meters (as stored/streamed) to a display string in
// feet. Returns an em dash for anything that isn't a finite number so a
// missing/null altitude never renders as "0 ft".
// ---------------------------------------------------------------------------
const METERS_TO_FEET = 3.28084;

/**
 * @param {number} meters
 * @returns {string}
 */
export function formatAltitudeFt(meters) {
  if (!Number.isFinite(meters)) return '—';
  return `${Math.round(meters * METERS_TO_FEET).toLocaleString()} ft`;
}

// ---------------------------------------------------------------------------
// Pluralization — superset of the three near-identical local implementations
// previously in headline.js, region-headlines.js, and rap-sheet-format.js.
// All three had the signature (n, singular, pluralForm) => n === 1 ? singular
// : pluralForm, so this is a straight extraction, not a behavior change.
// ---------------------------------------------------------------------------
/**
 * @param {number} n
 * @param {string} singular
 * @param {string} [pluralForm]
 * @returns {string}
 */
export function plural(n, singular, pluralForm) {
  return n === 1 ? singular : pluralForm;
}

// ---------------------------------------------------------------------------
// Superlative floor — ratio-based "behavior" claims (e.g. "works nights",
// "heavy night activity") pass trivially at n=1 (1/1 = 100%). Gate those
// templates on a minimum sample size in addition to their ratio.
// ---------------------------------------------------------------------------
export const MIN_EVENTS_FOR_BEHAVIOR_CLAIM = 3;

// ---------------------------------------------------------------------------
// Live-poll cadence disclosure — rendered as small print next to LIVE
// indicators so "LIVE" doesn't imply push/websocket immediacy.
// ---------------------------------------------------------------------------
export const LIVE_CADENCE_LABEL = 'updated ~2 min';
